import type { Building } from "building-types/building/buildingTypes";
import { InvalidPlanarGraphLoopsError } from "building-types/errors/buildingGenerationError";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import objectMap from "common/utils/objectMap";
import { resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { aabb2StrictlyContainsAABB2 } from "geom/aabb2";
import { getPointInPolygon } from "geom/containment";
import { COMPACTNESS_EPSILON, WindingDir } from "geom/core";
import { planarGraphHalfEdgeStripToPolyLine2 } from "geom/planarGraph";
import type { Point, Segment } from "geom/polyclip/dcel";
import { DEFAULT_SNAP_ROUND_PRECISION } from "geom/polyclip/dcelFromPolygons";
import { pointInPolygon } from "geom/polyclip/pointInPolygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polygonPlanarGraphNewDebug, polygonSegmentsPlanarGraphNew } from "geom/polygonPlanarGraph";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2CompactnessPolsbyPopper,
  polyLine2CopySimplify,
  polyLine2WindingDir,
  polyLine2sAreCoincident,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { RoomIndexImpl } from "../elements/rooms/roomIndexImpl";
import { getHorizontalAssemblyPerimeterInfo } from "../generationTools/generatePlanarGraphTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  levels,
  datumAbstractions,
  optionChoices,
  entityDisabledByGroup,
  mirror,
  mirrorTranslationX,
  rooms,
  enabledFeatures,
  floorBoundaries,
  horizontalAssemblyFoundations: foundations,
  horizontalAssemblyFoundationSystems: foundationSystems,
  horizontalAssemblyFloorSystems: floorSystems,
  horizontalAssemblyFloors: floors,
  doc: { optionSets, instanceOptionSets, foundationComponents, foundationAssemblies },
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    { foundations, foundationSystems, floorSystems, floors },
    definitions,
    optionChoices,
  );
  return {
    levels,
    foundationSettings: settings.foundation,
    foundationComponents,
    datumAbstractions,
    doorSettings: settings.door,
    foundationAssemblies,
    foundations,
    foundationSystems,
    floorSystems,
    floors,
    floorBoundaries,
    optionChoices: resolvedOptionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
    rooms,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  rooms: "shallow",
  levels: "shallow",
  foundationSettings: "shallow",
  optionChoices: "shallow",
  enabledFeatures: "shallow",
  floorBoundaries: "shallow",
  floors: "shallow",
  floorSystems: "shallow",
  foundations: "shallow",
  foundationSystems: "shallow",
});

function generate(input: ReturnType<typeof select>): {
  roomLevelIndices: Table<number>;
  rooms: Building["rooms"]; // this stage may update the rooms table when an room overlap issue is detectedπ
  roomsByLevel: Building["roomsByLevel"];
  planarGraphs: Building["planarGraphs"];
  roomIndex: Building["roomIndex"];
  issues: Building["issues"];
} {
  const { levels, rooms: inputRooms } = input;
  let roomsRW = inputRooms as RWTable<Room>;

  const issues: BuildingGenerationIssue[] = [];
  let originalRoomCount = 0;
  let planarGraphs: RWTable<PolygonPlanarGraph> = {};
  const { segmentsTable } = getHorizontalAssemblyPerimeterInfo(input);
  const useDCEL = false;

  /**
   * Remove overlapping rooms from the rooms table and rebuild the planar graph for the level.
   * Returns the new planar graph so the caller can reassign the local `graph` variable.
   */
  function removeOverlappingRooms(handles: RoomHandle[], levelId: string): PolygonPlanarGraph {
    roomsRW = { ...roomsRW };
    for (const handle of handles) {
      delete roomsRW[handle.id];
    }

    const filteredRoomOutlines = Object.values(roomsRW)
      .filter((x) => x.level.id === levelId)
      .map((x) => x.outline);

    const [newGraph] = polygonPlanarGraphNewDebug(filteredRoomOutlines, true, useDCEL);

    planarGraphs[levelId] = newGraph;
    return newGraph;
  }

  // multiple iterations are needed to resolve all the overlapping issues
  do {
    const currIssues: BuildingGenerationIssue[] = [];
    const originalRoomArray = Object.values(roomsRW);
    originalRoomCount = originalRoomArray.length;

    planarGraphs = {};

    for (const levelId in levels) {
      const roomsOnLevel = originalRoomArray.filter((x) => x.level.id === levelId);
      const roomOutlinesOnLevel = roomsOnLevel.map((r) => r.outline);

      let [graph] = polygonPlanarGraphNewDebug(roomOutlinesOnLevel, true, useDCEL);

      planarGraphs[levelId] = graph;

      // the total area of planar graph inner faces and the polygon input must be the same
      const TOTAL_AREA_TOLERANCE = 10;

      const roomAreas = roomOutlinesOnLevel.map(polyLine2Area);
      const windings = graph.planarGraph.faceWindingDirs;

      let totalPlanarGraphInnerFaceArea = 0;
      const planarGraphFaceAreas: number[] = [];

      const allLoops: PolyLine2[] = [];
      const invalidLoops: PolyLine2[] = [];

      let ccwFaceCount = 0;

      for (let i = 0, l = windings.length; i < l; i++) {
        const pl = planarGraphHalfEdgeStripToPolyLine2(graph.planarGraph, graph.planarGraph.faceHalfEdges[i]);
        const area = polyLine2Area(pl);
        planarGraphFaceAreas[i] = area;

        if (windings[i] === WindingDir.CCW) {
          totalPlanarGraphInnerFaceArea += area;
          ccwFaceCount += 1;
        }

        allLoops.push(pl);

        const compactness = polyLine2CompactnessPolsbyPopper(pl);
        if (compactness < COMPACTNESS_EPSILON) {
          invalidLoops.push(pl);
        }
      }

      const totalRoomArea = roomAreas.reduce((a, b) => a + b, 0);

      if (
        // CCW faces are internal faces of the planar graph. They should be 1:1 with the rooms.
        // This protects from overlapping rooms.
        ccwFaceCount !== roomsOnLevel.length ||
        // The internal room area should be preserved. This protects from holes on the level.
        Math.abs(totalRoomArea - totalPlanarGraphInnerFaceArea) > TOTAL_AREA_TOLERANCE
      ) {
        const roomHandles: RoomHandle[] = [];
        const roomFaceIndices = new Set<number>();

        // find the rooms with changed areas
        for (let i = 0, l = roomsOnLevel.length; i < l; i++) {
          const faceIndex = graph.polygonToPlanarGraphFace(i);
          roomFaceIndices.add(faceIndex);

          const roomArea = roomAreas[i];
          const planarGraphFaceArea = planarGraphFaceAreas[faceIndex];

          if (Math.abs(roomArea - planarGraphFaceArea) > TOTAL_AREA_TOLERANCE / l) {
            roomHandles.push(roomsOnLevel[i].roomHandle);
          }
        }

        // Filter out room faces, leaving only non-room loops (invalid, perimeter, holes)
        const remainingLoops = allLoops.filter((_, idx) => !roomFaceIndices.has(idx));

        let isRoomHole = false;

        if (roomHandles.length === 0) {
          const holes = remainingLoops.filter(
            (p) => !invalidLoops.includes(p) && polyLine2WindingDir(p) === WindingDir.CCW,
          );

          // no holes and no rooms w/ changed areas, this implies a conincident rooms condition
          if (holes.length === 0) {
            // let's see if we can find the coincident rooms!
            const added = {};
            const simplifiedOutlines = roomsOnLevel.map((x) => polyLine2CopySimplify(x.outline));

            for (let i = 0, l = roomsOnLevel.length; i < l; i++) {
              const { id: aId } = roomsOnLevel[i];
              const aSimplifiedOutline = simplifiedOutlines[i];

              for (let j = i + 1; j < l; j++) {
                const { id: bId } = roomsOnLevel[j];
                const bSimplifiedOutline = simplifiedOutlines[j];

                if (polyLine2sAreCoincident(aSimplifiedOutline, bSimplifiedOutline)) {
                  if (!added[aId]) {
                    roomHandles.push({ type: "ROOM", id: aId });
                    added[aId] = true;
                  }

                  if (!added[bId]) {
                    roomHandles.push({ type: "ROOM", id: bId });
                    added[bId] = true;
                  }
                }
              }
            }

            graph = removeOverlappingRooms(roomHandles, levelId);

            currIssues.push({
              type: BuildingGenerationIssueType.ROOM_OVERLAP,
              level: levels[levelId].handle,
              handle: roomHandles,
            });

            continue;
          }

          isRoomHole = true;

          if (roomHandles.length) {
            currIssues.push({
              type: BuildingGenerationIssueType.ROOM_OVERLAP,
              level: levels[levelId].handle,
              handle: roomHandles,
            });
          }

          currIssues.push({
            type: BuildingGenerationIssueType.HOLE_ON_LEVEL,
            handle: levels[levelId].handle,
            holes,
          });
        }

        if (roomHandles.length > 0) {
          graph = removeOverlappingRooms(roomHandles, levelId);

          if (!isRoomHole) {
            currIssues.push({
              type: BuildingGenerationIssueType.ROOM_OVERLAP,
              level: levels[levelId].handle,
              handle: roomHandles,
            });
          }
        }
      }

      // Check for rooms fully contained within other rooms.
      // The planar graph doesn't detect this case because non-intersecting nested
      // polygons create independent face structures with matching face counts and areas.
      if (roomsOnLevel.length >= 2) {
        const containedRoomHandles: RoomHandle[] = [];
        const containedRoomIds = new Set<string>();
        const roomAABBs = roomsOnLevel.map((r) => polyLine2AABB2(r.outline));

        for (let i = 0, l = roomsOnLevel.length; i < l; i++) {
          for (let j = i + 1; j < l; j++) {
            // AABB pre-filter: skip pairs where neither AABB strictly contains the other.
            // Strict containment (with DIST_EPSILON gap) prevents false positives from
            // adjacent rooms whose shared edges differ by ~1e-12 due to floating-point noise.
            const iContainsJ = aabb2StrictlyContainsAABB2(roomAABBs[i], roomAABBs[j]);
            const jContainsI = aabb2StrictlyContainsAABB2(roomAABBs[j], roomAABBs[i]);

            if (!iContainsJ && !jContainsI) {
              continue;
            }

            const outerIdx = iContainsJ ? i : j;
            const innerIdx = iContainsJ ? j : i;
            const innerRoom = roomsOnLevel[innerIdx];

            if (containedRoomIds.has(innerRoom.id)) {
              continue;
            }

            // Confirm with point-in-polygon using a point guaranteed to be inside
            // the inner room. A centroid would be unreliable here because concave
            // rooms (e.g. L-shapes) can have centroids outside the polygon itself.
            const innerPoint = getPointInPolygon(innerRoom.outline);
            if (!innerPoint) {
              continue;
            }

            const outerOutline = roomsOnLevel[outerIdx].outline;
            const outerLoop: Point[] = [];
            for (let k = 0, ol = outerOutline.length - 2; k < ol; k += 2) {
              outerLoop.push([outerOutline[k], outerOutline[k + 1]]);
            }

            if (pointInPolygon(innerPoint as Point, outerLoop)) {
              containedRoomHandles.push(innerRoom.roomHandle);
              containedRoomIds.add(innerRoom.id);
            }
          }
        }

        if (containedRoomHandles.length > 0) {
          graph = removeOverlappingRooms(containedRoomHandles, levelId);

          currIssues.push({
            type: BuildingGenerationIssueType.ROOM_OVERLAP,
            level: levels[levelId].handle,
            handle: containedRoomHandles,
          });
        }
      }

      if (invalidLoops.length) {
        throw new InvalidPlanarGraphLoopsError(levels[levelId].handle, invalidLoops);
      }
    }

    issues.push(...currIssues);

    // iterate until no more rooms are removed
  } while (originalRoomCount !== Object.keys(roomsRW).length);

  /**
   * @note we only loop below if there are foundation/floor perimeter segments to consider so we can use the constrained DCEL function
   */
  for (const graphId in segmentsTable) {
    const entries = segmentsTable[graphId];
    const freeSegments = entries.map((x): Segment => [x.segment[0], x.segment[1], x.segment[2], x.segment[3]]);
    const segmentPolygonIds = entries.map((x) => x.polygonIndex ?? 0);
    const [graph] = polygonSegmentsPlanarGraphNew([], freeSegments, segmentPolygonIds, DEFAULT_SNAP_ROUND_PRECISION);
    planarGraphs[graphId] = graph;
  }

  const roomLevelIndices: RWTable<number> = {};
  const roomsByLevel = objectMap(levels, () => [] as string[]);

  for (const roomId in roomsRW) {
    const room = roomsRW[roomId];
    const levelId = room.level.id;

    roomLevelIndices[roomId] = roomsByLevel[levelId].length;
    roomsByLevel[levelId].push(roomId);
  }

  const roomIndex = new RoomIndexImpl(roomsRW, roomLevelIndices, roomsByLevel, planarGraphs);

  return { rooms: roomsRW, roomLevelIndices, roomsByLevel, planarGraphs, roomIndex, issues };
}

export default memo(select, generate, compare);
